import { useEffect, useState } from 'react'
import { AlertTriangle, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input, Select } from '@/components/ui/input'
import { TranslatedInput } from '@/components/TranslatedField'
import {
  deleteAttribute,
  saveAttribute,
  saveCategory,
  subscribeToAttributes,
} from '@/features/materials/categories-api'
import { describeWriteError } from '@/features/users/permissions'
import { categoryGroupNames } from '@/lib/category-groups'
import { COST_TYPES, COST_TYPE_LABELS, TRANSLATION_LANGUAGES } from '@/lib/constants'
import type {
  AttributeType,
  CostType,
  MaterialCategory,
  MaterialCategoryAttribute,
} from '@/lib/types'

/**
 * Category editor: the category itself plus its attributes, in one dialog.
 *
 * Attributes are saved one by one as they are added or changed (each is its own document), which is
 * how the app behaves too — there is no "save everything" transaction to mimic.
 */
export function CategoryEditDialog({
  category,
  categories,
  actor,
  onClose,
}: {
  /** `null` = create a new category. */
  category: MaterialCategory | null
  /** The whole catalog — only to suggest the group names already in use. */
  categories: readonly MaterialCategory[]
  actor: { uid: string; label: string }
  onClose: () => void
}) {
  const [name, setName] = useState(category?.name ?? '')
  const [groupName, setGroupName] = useState(category?.groupName ?? '')
  const groupNames = categoryGroupNames(categories)
  const [costType, setCostType] = useState<CostType>(category?.costType ?? 'MATERIAL')
  const [nameI18n, setNameI18n] = useState<Record<string, string>>(category?.nameI18n ?? {})
  const [isSaving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await saveCategory(
        category?.id ?? null,
        {
          name: name.trim(),
          nameI18n: sanitize(nameI18n),
          costType,
          groupName,
        },
        actor,
      )
      onClose()
    } catch (caught) {
      setError(describeWriteError(caught))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{category ? 'Категория' : 'Новая категория'}</DialogTitle>
          <DialogDescription>
            Название пишется по-русски; переводы — необязательные переопределения. Новая категория
            встаёт в конец списка; нумерацию можно выровнять в «Несоответствиях».
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Название (ru)</span>
            <Input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Группа</span>
              <Input
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
                list="category-group-names"
                placeholder="Без группы"
              />
              <datalist id="category-group-names">
                {groupNames.map((value) => (
                  <option key={value} value={value} />
                ))}
              </datalist>
              <span className="text-muted-foreground text-xs">
                Необязательно: категории одной группы показываются вместе. Только для списков — на
                расчёт не влияет.
              </span>
            </label>
            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Тип затрат</span>
              <Select
                value={costType}
                onChange={(event) => setCostType(event.target.value as CostType)}
              >
                {COST_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {COST_TYPE_LABELS[value]}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="grid gap-2 rounded-lg border p-3">
            <span className="text-sm font-medium">Переводы названия</span>
            {TRANSLATION_LANGUAGES.map((language) => (
              <TranslatedInput
                key={language.tag}
                value={nameI18n[language.tag] ?? ''}
                onChange={(event) =>
                  setNameI18n({ ...nameI18n, [language.tag]: event.target.value })
                }
                source={name}
                lang={language.tag}
                onTranslated={(text) => setNameI18n((prev) => ({ ...prev, [language.tag]: text }))}
                placeholder={language.label}
                aria-label={`Название (${language.label})`}
              />
            ))}
          </div>

          {category ? (
            <AttributesEditor categoryId={category.id} />
          ) : (
            <p className="text-muted-foreground text-xs">
              Атрибуты можно будет добавить после сохранения категории.
            </p>
          )}

          {error ? (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>Не сохранено</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Закрыть
          </Button>
          <Button onClick={() => void handleSave()} disabled={name.trim().length === 0 || isSaving}>
            {isSaving ? <Loader2 className="animate-spin" /> : null}
            Сохранить категорию
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The attribute code is referenced from logic boxes as `{attr:CODE}` — same rule as the app. */
const ATTRIBUTE_CODE_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/

/**
 * Attributes of a saved category: live list, add, edit (name, translations, code, type) and delete.
 * The name follows the catalog convention: a base value (ru) plus optional per-language overrides in
 * `nameI18n`, which the app shows in the user's language. Validation mirrors
 * `MaterialCategoryEditViewModel` (name and code required, code pattern, code unique in the category).
 */
function AttributesEditor({ categoryId }: { categoryId: string }) {
  const [attributes, setAttributes] = useState<MaterialCategoryAttribute[]>([])
  const [error, setError] = useState<string | null>(null)
  /** `null` = the form adds a new attribute; otherwise the id of the attribute being edited. */
  const [editingId, setEditingId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [nameI18n, setNameI18n] = useState<Record<string, string>>({})
  const [code, setCode] = useState('')
  const [type, setType] = useState<AttributeType>('NUMBER')
  const [isBusy, setBusy] = useState(false)

  useEffect(
    () => subscribeToAttributes(categoryId, setAttributes, (caught) => setError(caught.message)),
    [categoryId],
  )

  const resetForm = () => {
    setEditingId(null)
    setName('')
    setNameI18n({})
    setCode('')
    setType('NUMBER')
  }

  const startEdit = (attribute: MaterialCategoryAttribute) => {
    setError(null)
    setEditingId(attribute.id)
    setName(attribute.name)
    setNameI18n(attribute.nameI18n)
    setCode(attribute.code)
    setType(attribute.type)
  }

  const trimmedCode = code.trim()
  const codeError =
    trimmedCode.length === 0
      ? null
      : !ATTRIBUTE_CODE_PATTERN.test(trimmedCode)
        ? 'Код: латинские буквы, цифры и _, не с цифры.'
        : attributes.some(
              (item) =>
                item.id !== editingId && item.code.trim().toLowerCase() === trimmedCode.toLowerCase(),
            )
          ? 'Такой код уже есть у другого атрибута этой категории.'
          : null
  const canSubmit = name.trim().length > 0 && trimmedCode.length > 0 && codeError === null

  const handleSubmit = async () => {
    if (!canSubmit) return
    const editing = attributes.find((item) => item.id === editingId) ?? null
    setBusy(true)
    setError(null)
    try {
      await saveAttribute(editing?.id ?? null, {
        categoryId,
        name: name.trim(),
        nameI18n: sanitize(nameI18n),
        code: trimmedCode,
        type,
        // A new attribute goes to the end; an edited one keeps its place.
        orderIndex: editing?.orderIndex ?? attributes.length,
      })
      resetForm()
    } catch (caught) {
      setError(describeWriteError(caught))
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (id: string) => {
    setBusy(true)
    setError(null)
    try {
      await deleteAttribute(id)
      if (id === editingId) resetForm()
    } catch (caught) {
      setError(describeWriteError(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid gap-3 rounded-lg border p-3">
      <span className="text-sm font-medium">Атрибуты категории</span>

      {attributes.length === 0 ? (
        <p className="text-muted-foreground text-xs">Атрибутов нет.</p>
      ) : (
        <ul className="grid gap-1.5">
          {attributes.map((attribute) => {
            const translations = TRANSLATION_LANGUAGES.map(
              (language) => attribute.nameI18n[language.tag],
            ).filter((value): value is string => Boolean(value))
            return (
              <li
                key={attribute.id}
                className={`flex items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm ${
                  attribute.id === editingId ? 'border-primary' : ''
                }`}
              >
                <span className="min-w-0 truncate">
                  {attribute.name}
                  <span className="text-muted-foreground">
                    {attribute.code ? ` · ${attribute.code}` : ''} ·{' '}
                    {attribute.type === 'NUMBER' ? 'число' : 'текст'}
                    {translations.length > 0 ? ` · ${translations.join(' / ')}` : ''}
                  </span>
                </span>
                <span className="flex shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => startEdit(attribute)}
                    aria-label={`Изменить атрибут ${attribute.name}`}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    disabled={isBusy}
                    onClick={() => void handleDelete(attribute.id)}
                    aria-label={`Удалить атрибут ${attribute.name}`}
                  >
                    <Trash2 />
                  </Button>
                </span>
              </li>
            )
          })}
        </ul>
      )}

      <div className="grid gap-2 rounded-md border border-dashed p-2">
        <span className="text-xs font-medium">
          {editingId ? 'Изменение атрибута' : 'Новый атрибут'}
        </span>
        <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Название атрибута (ru)"
            aria-label="Название атрибута"
          />
          <Input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="Код"
            className="font-mono sm:w-28"
            aria-label="Код атрибута"
            aria-invalid={codeError !== null}
          />
          <Select
            value={type}
            onChange={(event) => setType(event.target.value as AttributeType)}
            className="sm:w-28"
            aria-label="Тип атрибута"
          >
            <option value="NUMBER">число</option>
            <option value="STRING">текст</option>
          </Select>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {TRANSLATION_LANGUAGES.map((language) => (
            <TranslatedInput
              key={language.tag}
              value={nameI18n[language.tag] ?? ''}
              onChange={(event) => setNameI18n({ ...nameI18n, [language.tag]: event.target.value })}
              source={name}
              lang={language.tag}
              onTranslated={(text) => setNameI18n((prev) => ({ ...prev, [language.tag]: text }))}
              placeholder={`Название · ${language.label}`}
              aria-label={`Название атрибута (${language.label})`}
            />
          ))}
        </div>
        {codeError ? <span className="text-destructive text-xs">{codeError}</span> : null}
        {editingId ? (
          <span className="text-muted-foreground text-xs">
            Смена кода ломает ссылки <code>{'{attr:КОД}'}</code> в эффектах и значения у материалов.
          </span>
        ) : null}
        <div className="flex justify-end gap-2">
          {editingId ? (
            <Button variant="ghost" size="sm" onClick={resetForm} disabled={isBusy}>
              Отмена
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleSubmit()}
            disabled={isBusy || !canSubmit}
          >
            {editingId ? null : <Plus />}
            {editingId ? 'Сохранить' : 'Добавить'}
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  )
}

function sanitize(values: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [tag, value] of Object.entries(values)) {
    const trimmed = value.trim()
    if (trimmed.length > 0) result[tag] = trimmed
  }
  return result
}
